import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Lang = "en" | "bn";
export type Theme = "light" | "dark";

interface AppState {
  lang: Lang;
  /** True once the user has explicitly chosen a language. Disables auto-detect. */
  langTouched: boolean;
  theme: Theme;
  setLang: (l: Lang) => void;
  toggleLang: () => void;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  /** Detect from navigator.language on first visit. No-op if user already chose. */
  autoDetectLang: () => void;
}

function applyTheme(theme: Theme) {
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }
}

function applyLangAttr(lang: Lang) {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
  }
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Default for BD audience — overridden by browser detect on first visit
      lang: "bn",
      langTouched: false,
      theme: "light",
      setLang: (lang) => {
        applyLangAttr(lang);
        set({ lang, langTouched: true });
      },
      toggleLang: () => {
        const next: Lang = get().lang === "en" ? "bn" : "en";
        applyLangAttr(next);
        set({ lang: next, langTouched: true });
      },
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        const next: Theme = get().theme === "light" ? "dark" : "light";
        applyTheme(next);
        set({ theme: next });
      },
      autoDetectLang: () => {
        if (get().langTouched) return;
        if (typeof navigator === "undefined") return;
        const langs = (navigator.languages?.length ? navigator.languages : [navigator.language]) ?? [];
        const lower = langs.map((l) => l.toLowerCase());
        // Prefer Bengali if present anywhere in the user's language list
        const wantsBn = lower.some((l) => l.startsWith("bn"));
        const wantsEn = lower.some((l) => l.startsWith("en"));
        const detected: Lang = wantsBn ? "bn" : wantsEn ? "en" : "bn"; // BD fallback
        applyLangAttr(detected);
        set({ lang: detected });
      },
    }),
    {
      name: "bangla-calc-prefs",
      onRehydrateStorage: () => (state) => {
        if (state) {
          applyTheme(state.theme);
          applyLangAttr(state.lang);
        }
      },
    },
  ),
);
